import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import DeleteIcon from "@mui/icons-material/Delete";
import { makeStyles } from "@mui/styles";
import ConfirmBox from "../general/confirmBox";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    width: "auto",
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "calc(100vh - 230px)",
   // marginTop: "-70px",
  },
  tableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "var(--primary-light-blue-color)",
      color: "#000",
    },
    "& td": {
      padding: "2px 4px 2px 10px",
    },
  },
  headerRow: {
    backgroundColor: "var(--primary-blue-color)",
    color: "#ffffff !important",
    "& th": {
      padding: "9px",
      color: "#fff",
    },
  },
  headerColumn: {
    color: "#ffffff !important",
    "&:hover": {
      color: "#000",
    },
  },
  oddRow: {
    backgroundColor: "#eeeeee",
    opacity: "100%",
  },
  evenRow: {
    backgroundColor: "#dddddd",
    opacity: "100%",
  },
}));

const UserList = ({
  users,
  searchText,
  onUserSelect,
  onUserDelete,
  roles,
  loading,
}) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [showMessage, setShowMessage] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);

  const classes = useStyles();

  useEffect(() => {
    const fusers = users.filter(
      (user) =>
        (user.firstname && user.firstname.toLowerCase().includes(searchText)) ||
        (user.lastname && user.lastname.toLowerCase().includes(searchText)) ||
        (user.email && user.email.toLowerCase().includes(searchText)) ||
        (user.rolename && user.rolename.toLowerCase().includes(searchText))
    );

    let index =
      (page - 1) * itemsPerPage > fusers.length - 1
        ? 0
        : (page - 1) * itemsPerPage;
    setStartIndex(index);
    setEndIndex(index + itemsPerPage);

    setFilteredUsers(fusers);
    setTotalPages(Math.ceil(fusers.length / itemsPerPage));
  }, [searchText, users, page]);

  useEffect(() => {
    let index = (page - 1) * itemsPerPage;
    setStartIndex(index);
    setEndIndex(index + itemsPerPage);
  }, [page]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleCloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
    setSelectedUserId(null);
  };

  const deleteUserById = async (id) => {
    try {
      await onUserDelete(id);
      setShowMessage(true);
      setMessage("User deactivated successfully");
      setOpenConfirmDelete(false);
      setSelectedUserId(null);
    } catch (err) {
      console.log("error", err);
      setMessage(err?.message || "Failed to delete user");
      setShowMessage(true);
    }
  };

  const getRoleName = (roleId) => {
    const roleMap = {
      1: "Admin",
      2: "User",
      3: "Developer",
    };
    return roleMap[roleId] || "Unknown";
  };

  const getRoleColor = (roleId) => {
    const colorMap = {
      1: "error",
      2: "default",
      3: "secondary",
    };
    return colorMap[roleId] || "default";
  };

  if (!filteredUsers || filteredUsers.length === 0) {
    return (
      <div className="no-orders-found-text">
        <h3>No users found</h3>
        {showMessage && (
          <div>
            <Snackbar
              open={showMessage}
              autoHideDuration={1200}
              onClose={() => {
                setShowMessage(false);
              }}
              message="Done"
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}
            >
              <Alert
                severity={`${
                  message.includes("successfully") ? "success" : "error"
                }`}
                variant="filled"
              >
                {message}
              </Alert>
            </Snackbar>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <CircularProgress variant="determinate" value={50} valueBuffer={20} />
    );
  }

  return (
    <div>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.slice(startIndex, endIndex).map((user, index) => (
              <TableRow
                onClick={() => onUserSelect(user)}
                key={user.id}
                className={`${classes.tableRow} ${
                  user.isSelected
                    ? "highlighted-row"
                    : index % 2 === 0
                    ? classes.evenRow
                    : classes.oddRow
                }`}
              >
                <TableCell>
                  {user.firstname && user.lastname
                    ? `${user.firstname} ${user.lastname}`
                    : user.firstname || user.lastname || "—"}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell >
                  <Chip a
                    label={user.rolename || getRoleName(user.roleid)}
                    color={getRoleColor(user.roleid)}
                    size="small"
                   
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.isactive ? "Active" : "Inactive"}
                    color={user.isactive ? "success" : "default"}
                    size="small"
                   variant="filled" 
                  />
                </TableCell>
                <TableCell>
                  {user.lastlogindate
                    ? new Date(user.lastlogindate).toLocaleDateString()
                    : "Never"}
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label="delete"
                    style={{ color: "#990000" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUserId(user.id);
                      setOpenConfirmDelete(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        renderItem={(item) => (
          <PaginationItem component="button" type="button" {...item} />
        )}
      />
      {showMessage && (
        <div>
          <Snackbar
            open={showMessage}
            autoHideDuration={1200}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}
            onClose={() => {
              setShowMessage(false);
            }}
            message="Done"
          >
            <Alert
              severity={`${
                message.includes("successfully") ? "success" : "error"
              }`}
              variant="filled"
            >
              {message}
            </Alert>
          </Snackbar>
        </div>
      )}
      {openConfirmDelete && (
        <ConfirmBox
          confirmAction={() => {
            deleteUserById(selectedUserId);
          }}
          cancelAction={handleCloseConfirmDelete}
        />
      )}
    </div>
  );
};

export default UserList;